import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { appApi } from "../core/api/app.js";
import { clearAppToken, loadAppSession, saveAppToken } from "../core/appSession.js";

const AppSessionContext = createContext(null);

export function AppSessionProvider({ children }) {
  const [token, setToken] = useState(() => loadAppSession()?.token ?? null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    appApi.me(token).then(
      (data) => {
        if (cancelled) return;
        setMe(data);
        setError(null);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        if (err.status === 401) {
          clearAppToken();
          setToken(null);
        } else {
          setError(err.message);
        }
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [token]);

  const signIn = useCallback(async (code) => {
    const data = await appApi.exchange(code);
    saveAppToken(data.token);
    setToken(data.token);
    return data;
  }, []);

  const signOut = useCallback(async () => {
    if (token) await appApi.logout(token).catch(() => {});
    clearAppToken();
    setToken(null);
    setMe(null);
  }, [token]);

  const value = useMemo(
    () => ({ token, me, loading, error, signIn, signOut, isSignedIn: Boolean(token && me) }),
    [token, me, loading, error, signIn, signOut],
  );

  return <AppSessionContext.Provider value={value}>{children}</AppSessionContext.Provider>;
}

export function useAppSession() {
  const value = useContext(AppSessionContext);
  if (!value) throw new Error("useAppSession must be used inside an AppSessionProvider");
  return value;
}
